import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import mixpanel from '../../utilities/mixpanel';
import meterReplacementQuestions from '../../fixtures/meterReplacementQuestions.json';
import rtuMeterReplacementQuestions from '../../fixtures/rtuMeterReplacementQuestions.json';
import { METER_REPLACEMENT_INFO_ROUTE, RTU_METER_REPLACEMENT_INFO_ROUTE } from '../../constants/routes';
import OneColumn from '../../layouts/OneColumn';
import IndivFAQSection from '../sharedComponents/IndivFAQSection';
import PageContentCard from '../sharedComponents/PageContentCard';
import { SmallText } from '../sharedComponents/SharedElements';
import {
  VIEWED_PAGE,
  LEFT_FAQ_FEEDBACK_METER_REPLACEMENT_INFORMATION,
  VIEWED_FAQ_QUESTION_METER_REPLACEMENT_INFORMATION,
  LEFT_FAQ_FEEDBACK_RTU_METER_REPLACEMENT_INFORMATION,
  VIEWED_FAQ_QUESTION_RTU_METER_REPLACEMENT_INFORMATION,
} from '../../constants/mixpanelEventNames';

const MeterReplacementInformationPage = () => {
  const { pathname } = useLocation();

  const is2021MeterUpgradeProjectCaseOpen = pathname === METER_REPLACEMENT_INFO_ROUTE;
  const isRTU2021MeterUpgradeProjectCaseOpen = pathname === RTU_METER_REPLACEMENT_INFO_ROUTE;

  useEffect(() => {
    mixpanel.track(VIEWED_PAGE('Meter Replacement Information'), { route: pathname });
  }, []);

  const getMeterReplacementContent = () => {
    return (
      <>
        <SmallText>
          Various network providers have announced that they will be shutting down their 2G & 3G networks in 2022. To prepare for this, Sunrun has hired
          Southern Cross, our trusted Service Partner, to make the first attempt to upgrade your device at no cost to you.
        </SmallText>
        <SmallText>
          Southern Cross will be contacting you when they are in your neighborhood to complete the upgrade. If your equipment is not accessible (in a garage,
          behind locked gates, or if you have a dog) please contact Southern Cross directly to schedule an appointment. If Southern Cross was unable to upgrade
          your meter, Sunrun will contact you to schedule an appointment when our technicians are in the area.
        </SmallText>
        <SmallText>
          Due to supply chain shortages there is a delay in completing these upgrades. We apologize for this inconvenience. The good news is that your panels
          will continue to produce as expected, however, you will not be able to see that production in mySunrun until the upgrade has been completed.
        </SmallText>
        <SmallText>For all other questions please review the FAQs below or contact* Southern Cross directly.</SmallText>
        <SmallText>
          Southern Cross
          <br />
          866-909-6068
        </SmallText>
      </>
    );
  };

  const getMeterReplacementRTUContent = () => {
    return (
      <>
        <SmallText>
          Various network providers have announced that they will be shutting down their 2G & 3G networks in 2022. To prepare for this, Sunrun is upgrading
          devices to 4G at no cost to you.
        </SmallText>
        <SmallText>
          Due to supply chain shortages there is a delay in completing these upgrades. We apologize for this inconvenience. The good news is that your panels
          will continue to produce as expected, however, you will not be able to see that production in mySunrun until the upgrade has been completed. We will
          contact you to schedule an appointment as soon as we have the inventory to complete your upgrade.
        </SmallText>
        <SmallText>For all other questions please review the FAQs below.</SmallText>
      </>
    );
  };

  const getAsteriskText = () => {
    return (
      <>
        <div>
          *Please do not contact Southern Cross until they have notified you, via Postcard, that they will be in your neighborhood as they will not be able to
          schedule an appointment until they are in your area.
        </div>
      </>
    );
  };

  return (
    <>
      {is2021MeterUpgradeProjectCaseOpen && (
        <OneColumn>
          <PageContentCard
            customCardWidth="880px"
            backgroundImg
            cardHeader={<div>2G & 3G Communication Device Upgrade</div>}
            customAsterisk={getAsteriskText()}
            dataTestId="meter-replacement-information"
            enrollmentContent={getMeterReplacementContent()}
            pageHeader="On-Site System Maintenance"
          />
          <IndivFAQSection
            customCardWidth="880px"
            headerText="Device Upgrade FAQ:"
            dataTestId="meter-replacement-information-faq"
            leftFeedbackMixpanelMessage={LEFT_FAQ_FEEDBACK_METER_REPLACEMENT_INFORMATION}
            questions={meterReplacementQuestions}
            viewedQMixpanelMessage={VIEWED_FAQ_QUESTION_METER_REPLACEMENT_INFORMATION}
            route={METER_REPLACEMENT_INFO_ROUTE}
          />
        </OneColumn>
      )}
      {isRTU2021MeterUpgradeProjectCaseOpen && (
        <OneColumn>
          <PageContentCard
            customCardWidth="880px"
            backgroundImg
            cardHeader={<div>2G & 3G Communication Device Upgrade</div>}
            dataTestId="meter-replacement-information"
            enrollmentContent={getMeterReplacementRTUContent()}
            pageHeader="On-Site System Maintenance"
          />
          <IndivFAQSection
            customCardWidth="880px"
            headerText="Device Upgrade FAQ:"
            dataTestId="rtu-meter-replacement-information-faq"
            leftFeedbackMixpanelMessage={LEFT_FAQ_FEEDBACK_RTU_METER_REPLACEMENT_INFORMATION}
            questions={rtuMeterReplacementQuestions}
            viewedQMixpanelMessage={VIEWED_FAQ_QUESTION_RTU_METER_REPLACEMENT_INFORMATION}
            route={RTU_METER_REPLACEMENT_INFO_ROUTE}
          />
        </OneColumn>
      )}
    </>
  );
};

MeterReplacementInformationPage.defaultProps = {
  leapAuthId: null,
  onClick: () => {},
};

export default MeterReplacementInformationPage;
